import React, { useState, useEffect, useMemo } from "react";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import moment from "moment";
import { utility } from "../../../framework/utility/utilityProvider";
import * as API from '../../../framework/API/api';
import { TimePickerWithFormat } from "../../../framework/forms/helpercomponents/timepicker/TimePickerWithFormat";
import { DropdownWithDefaultValue } from "../../../framework/forms/helpercomponents/dropdown/DropdownWithDefaultValue";
import { ENTITYNAME, FrameEquivalentOfMilliSecond, MEDIACATEGORIES, MODULE, TOAST_TYPES } from "../../../framework/constant/constant";
import { useTranslation } from "../../../locale/useTranslation";
import { PlanningHelper } from "../helper/PlanningHelper";
import BossDialog from "../../../components/BossDialog";
import ToastNotification from "../../../components/ToastNotification";
import { ConfirmAlert } from "../../../ConfirmAlert";

const PlanningRightSideEditFormWithSlot = (props) => {
  const [channels, setChannels] = useState([]);
  const [segments, setSegments] = useState([]);
  const [isEpisode, setIsEpisode] = useState(props.dataItem?.mediaEpisode?.MediaCategory?.SID == MEDIACATEGORIES.Episode);
  const [isOpenFromSchedule, setIsOpenFromSchedule] = useState(props.isOpenFromSchedule ?? false);
  const lang = useTranslation();

  const [dataItem, setDataItem] = useState(props.dataItem);
  const [liveFormValues, setLiveFormValues] = useState({});

  const [windowSize, setWindowSize] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 1200,
    height: typeof window !== "undefined" ? window.innerHeight : 800,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  const initialValues = useMemo(() => ({
    ...props.dataItem,
    Title: props.dataItem?.mediaEpisode?.Title ?? props.dataItem?.Title ?? "",
    Description: props.dataItem?.mediaEpisode?.Description ?? props.dataItem?.Description ?? "",
    ActualDuration: utility.convertMilisecondsToStringWithFrames(props.dataItem?.ActualDuration ?? 0),
    NominalDuration: utility.convertMilisecondsToStringWithFrames(props.dataItem?.NominalDuration ?? 0),
    customDuration: props.dataItem?.customDuration ? utility.convertMilisecondsToStringWithFrames(props.dataItem?.customDuration ?? 0) : null,
    SeasonNumber: props.dataItem?.mediaEpisode?.SeasonNumber ?? 1,
    EpisodeNumber: props.dataItem?.mediaEpisode?.EpisodeNumber ?? 1,

    SlotDateLocal: new Date(utility.datetimeFormat(PlanningHelper.getGMTSlotDateTime(props.dataItem?.Channel, props.dataItem?.SlotDateTime, true))),
    SlotTimeLocal: utility.convertMilisecondsToStringWithFrames(PlanningHelper.getGMTSlotDateTime(props.dataItem?.Channel, props.dataItem?.SlotDateTime, true)),

    customTitle: props.dataItem?.customTitle ?? "",
    SlotSynopsis: props.dataItem?.SlotSynopsis ?? "",

    selectedSegment: props.dataItem?.Segment_id
  }), [props.dataItem]);

  useEffect(() => {
    setLiveFormValues(initialValues);
  }, [initialValues]);

  const tileTitle = props.dataItem?.mediaEpisode?.Title || props.dataItem?.Title || props.dataItem?.customTitle || "";
  const assetId = props.dataItem?.mediaEpisode?.AssetId || props.dataItem?.AssetId || "";
  const categoryName = props.dataItem?.mediaEpisode?.MediaCategory?.Description || props.dataItem?.MediaCategory?.Description || "";

  const channelName = useMemo(() => {
    const chKey = props.dataItem?.Channel?.SID ?? props.dataItem?.ChannelSID ?? props.dataItem?.Channel;
    const found = channels?.find((c) => c.SID === chKey || c.SID === props.dataItem?.Channel);
    if (found) {
      return found.FullChannelName || found.ChannelName || "";
    }
    if (typeof props.dataItem?.Channel === "object") {
      return props.dataItem?.Channel?.FullChannelName || props.dataItem?.Channel?.ChannelName || "";
    }
    return props.dataItem?.ChannelName || (typeof props.dataItem?.Channel === "string" ? props.dataItem?.Channel : "");
  }, [channels, props.dataItem]);

  const slotDateTimeStr = useMemo(() => {
    try {
      if (props.dataItem?.SlotDateTime && props.dataItem?.Channel) {
        const gmtSlot = PlanningHelper.getGMTSlotDateTime(props.dataItem.Channel, props.dataItem.SlotDateTime, true);
        if (gmtSlot) {
          const d = new Date(utility.datetimeFormat(gmtSlot));
          const dateStr = moment(d).isValid() ? moment(d).format("DD/MM/YYYY") : "";
          const timeStr = utility.convertMilisecondsToStringWithFrames(gmtSlot) || "";
          return `${dateStr} ${timeStr}`.trim();
        }
      }
      const dateVal = initialValues.SlotDateLocal ? moment(new Date(initialValues.SlotDateLocal)).format("DD/MM/YYYY") : "";
      const timeVal = initialValues.SlotTimeLocal || "";
      return `${dateVal} ${timeVal}`.trim();
    } catch (err) {
      return "";
    }
  }, [props.dataItem, initialValues]);

  useEffect(() => {
    setIsEpisode(props.dataItem?.mediaEpisode?.MediaCategory?.SID == MEDIACATEGORIES.Episode);
    loadMaster();
  }, [props.dataItem]);

  async function loadMaster() {
    var channelsData = await API.getDataLookup(ENTITYNAME.Channel);
    var segmentsData = props.dataItem?.mediaEpisode?.SID ? await API.getData(ENTITYNAME.MediaEpisodeSegment, {
      query: ['MediaEpisodeSID', '=', props.dataItem?.mediaEpisode?.SID],
    }) : { data: [] };

    setChannels(channelsData.data);
    let validSegments = PlanningHelper.getValidSegmentsByChannel(segmentsData.data || [], props.dataItem?.Channel);
    setSegments(validSegments);
  }

  const handleSubmit = async (event) => {
    let dayStartTime = PlanningHelper.getDayStartTime([props.dataItem.Channel]);

    let slotDateLocal = event.SlotDateLocal;
    if (dayStartTime > 0 && utility.convertStringWithFramesToMilliseconds(event.SlotTimeLocal) < dayStartTime) {
      slotDateLocal = new Date(new Date(event.SlotDateLocal).setDate(new Date(event.SlotDateLocal).getDate() + 1));
    }

    const [hours, minutes, seconds, frames] = event.SlotTimeLocal.replaceAll('_', '0').split(':');
    var slotDate = new Date(slotDateLocal).setHours(parseInt(hours), parseInt(minutes), parseInt(seconds), parseInt(frames) * FrameEquivalentOfMilliSecond);
    var newSlotDateTime = utility.getLocalDateTimeToUtcMiliseconds(new Date(slotDate));

    let updatedData = {
      SID: event.SID,
      _id: event._id,
      ScheduleDate: props?.module == MODULE.PLANNING ?
        PlanningHelper.getGMTSlotDateTime(props.dataItem.Channel, newSlotDateTime) :
        PlanningHelper.getGMTSlotDateTime(props.dataItem.Channel, utility.getLocalDateTimeToUtcMiliseconds(event.SlotDateLocal)),

      SlotDateTime: PlanningHelper.getGMTSlotDateTime(props.dataItem.Channel, newSlotDateTime),
      EndSlotDateTime: PlanningHelper.getGMTSlotDateTime(props.dataItem.Channel, newSlotDateTime + event.Duration),
      Segment_id: event.selectedSegment?._id ?? props.dataItem.Segment_id,
      customDuration: typeof event.customDuration === 'string'
        ? utility.convertStringWithFramesToMilliseconds(event.customDuration)
        : (event.customDuration ?? dataItem.customDuration ?? props.dataItem?.customDuration),
      segment: segments.find((seg) => seg._id == (event.selectedSegment?._id ?? props.dataItem.Segment_id)) ?? props.dataItem.segment,
      customTitle: event.customTitle,
      SlotSynopsis: event.SlotSynopsis,
      Description: event.SlotSynopsis ?? event.Description
    };

    const targetChannelSID = props.dataItem?.Channel?.SID ?? props.dataItem?.ChannelSID ?? props.dataItem?.Channel;
    const matchingSlot = PlanningHelper.getMatchingCustomSlot(
      updatedData.SlotDateTime,
      null,
      targetChannelSID,
      props.customSlots || []
    );

    const originalSlot = props.dataItem?.ProgramScheduleSlot;
    const wasInCustomSlot = Boolean(originalSlot && originalSlot._id);
    const isMovingOutOfCustomSlot = wasInCustomSlot && (
      !matchingSlot ||
      String(matchingSlot._id) !== String(originalSlot._id)
    );

    const executeSave = async (dataToSave) => {
      const validDayPart = await PlanningHelper.getValidDayPartForTimeBand(dataToSave);

      let res = props.module == MODULE.SCHEDULING ? { success: true } : await API.savePlanning(dataToSave, segments, false, false, 0, validDayPart);

      if (res.success) {
        dataToSave.NominalDuration = (typeof event.NominalDuration == 'string' ? utility.convertStringWithFramesToMilliseconds(event.NominalDuration) : event.NominalDuration);
        dataToSave.ActualDuration = (typeof event.ActualDuration == 'string' ? utility.convertStringWithFramesToMilliseconds(event.ActualDuration) : event.ActualDuration);

        if (dataToSave.Segment_id) {
          let segment = segments.find((seg) => seg._id == dataToSave.Segment_id);
          if (segment) {
            dataToSave.ActualDuration = segment.ActualDuration;
          }
        }

        props.onUpdateFromPlanningEditForm(dataToSave);
        props.onClose();
      } else {
        ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: res.message });
      }
    };

    if (isMovingOutOfCustomSlot) {
      const slotName = originalSlot?.Description || "Custom Slot";
      ConfirmAlert(
        async () => {
          updatedData.ProgramScheduleSlot = matchingSlot ? {
            _id: matchingSlot._id?.toString() || matchingSlot._id,
            Description: matchingSlot.Description || ""
          } : null;
          await executeSave(updatedData);
        },
        () => {
          // Abort save when user chooses not to detach
        },
        "Detach Slot",
        `Do you want to detach the planning item from this slot (${slotName})?`
      );
    } else {
      updatedData.ProgramScheduleSlot = matchingSlot ? {
        _id: matchingSlot._id?.toString() || matchingSlot._id,
        Description: matchingSlot.Description || ""
      } : (props.dataItem?.ProgramScheduleSlot ?? null);
      await executeSave(updatedData);
    }
  };

  return (
    <BossDialog
      title={initialValues.Title}
      onClose={props.onClose}
      width={"26.7%"}
      height={"100vh"}
      overlayClassName={"right-drawer"}
      overlayStyle={{ pointerEvents: 'none', background: 'none' }}
      style={{
        pointerEvents: 'auto',
        height: "100vh",
        maxHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        borderRadius: 0,
        overflowY: "auto",
      }}
    >
      <Form
        key={props.dataItem._id}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        onChange={(formState) => {
          if (formState && formState.values) {
            setLiveFormValues({ ...formState.values });
          }
        }}
        render={(formRenderProps) => {
          const values = { ...initialValues, ...liveFormValues, ...(formRenderProps.values || {}) };

          const liveCustomTitle = values.customTitle;
          const liveTitle = values.Title;
          const displayTitle =
            liveCustomTitle !== undefined && liveCustomTitle !== null && String(liveCustomTitle).trim() !== ""
              ? String(liveCustomTitle).trim()
              : liveTitle !== undefined && liveTitle !== null && String(liveTitle).trim() !== ""
                ? String(liveTitle).trim()
                : tileTitle;

          const displayAssetId =
            values.mediaEpisode?.AssetId ||
            values.AssetId ||
            assetId;

          const displayCategoryName =
            values.mediaEpisode?.MediaCategory?.Description ||
            values.MediaCategory?.Description ||
            categoryName;

          const currentSlotDate = values.SlotDateLocal;
          const currentSlotTime = values.SlotTimeLocal;

          let liveDateStr = "";
          if (currentSlotDate) {
            const d = new Date(currentSlotDate);
            if (moment(d).isValid()) {
              liveDateStr = moment(d).format("DD/MM/YYYY");
            }
          }

          let liveTimeStr = "";
          if (currentSlotTime !== undefined && currentSlotTime !== null && String(currentSlotTime).trim() !== "") {
            liveTimeStr = String(currentSlotTime).replaceAll("_", "0").trim();
          }

          const displaySlotDateTime =
            liveDateStr || liveTimeStr
              ? `${liveDateStr} ${liveTimeStr}`.trim()
              : slotDateTimeStr;

          const currentChannelKey =
            values.ChannelSID ??
            values.Channel?.SID ??
            values.Channel ??
            props.dataItem?.Channel?.SID ??
            props.dataItem?.ChannelSID ??
            props.dataItem?.Channel;

          const foundChannel = channels?.find(
            (c) => c.SID === currentChannelKey || c.SID === props.dataItem?.Channel
          );

          const displayChannelName = foundChannel
            ? foundChannel.FullChannelName || foundChannel.ChannelName || ""
            : typeof values.Channel === "object"
              ? values.Channel?.FullChannelName || values.Channel?.ChannelName || ""
              : channelName;

          const currentCustomDur = values.customDuration;
          const currentNominalDur = values.NominalDuration;
          const currentActualDur = values.ActualDuration;

          let displayDuration = "";
          if (currentCustomDur) {
            displayDuration = typeof currentCustomDur === "number"
              ? utility.convertMilisecondsToStringWithFrames(currentCustomDur)
              : String(currentCustomDur).replaceAll("_", "0");
          } else if (currentNominalDur) {
            displayDuration = typeof currentNominalDur === "number"
              ? utility.convertMilisecondsToStringWithFrames(currentNominalDur)
              : String(currentNominalDur).replaceAll("_", "0");
          } else if (currentActualDur) {
            displayDuration = typeof currentActualDur === "number"
              ? utility.convertMilisecondsToStringWithFrames(currentActualDur)
              : String(currentActualDur).replaceAll("_", "0");
          }

          const currentSegmentVal = values.selectedSegment;
          let displaySegmentName = "";
          if (currentSegmentVal) {
            const segObj = typeof currentSegmentVal === "object"
              ? currentSegmentVal
              : segments.find((s) => s._id === currentSegmentVal || s.SID === currentSegmentVal);
            if (segObj) {
              displaySegmentName = segObj.SegmentType?.Description || segObj.Description || "";
            }
          }

          const liveSynopsis = values.SlotSynopsis;
          const liveDesc = values.Description;
          const displaySynopsis =
            liveSynopsis !== undefined && liveSynopsis !== null && String(liveSynopsis).trim() !== ""
              ? String(liveSynopsis).trim()
              : liveDesc !== undefined && liveDesc !== null && String(liveDesc).trim() !== ""
                ? String(liveDesc).trim()
                : "";

          const seasonNum = values.SeasonNumber ?? props.dataItem?.mediaEpisode?.SeasonNumber;
          const epNum = values.EpisodeNumber ?? props.dataItem?.mediaEpisode?.EpisodeNumber;
          let displaySeasonEp = "";
          if (isEpisode) {
            const sStr = (seasonNum !== undefined && seasonNum !== null) ? `S${seasonNum}` : "";
            const eStr = (epNum !== undefined && epNum !== null) ? `E${epNum}` : "";
            displaySeasonEp = [sStr, eStr].filter(Boolean).join(" ");
          }

          return (
            <FormElement style={{ display: "flex", flexDirection: "column", flex: 1, overflowY: "auto" }}>
              <div className={"col-12 my-2 d-flex gap-2"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                <button
                  type={"submit"}
                  className={"k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary flex-grow-1 mr-1"}
                >
                  {lang.save_button_text}
                </button>
                <button
                  className={"k-button k-button-md k-rounded-md k-button-solid k-button-solid-base flex-grow-1 ml-1"}
                  onClick={() => {
                    props.onClose();
                  }}
                >
                  {lang.cancel_button_text}
                </button>
              </div>
              <div
                className="mb-3"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                  padding: "12px 16px",
                  backgroundColor: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                }}
              >
                {/* Row 1: Title */}
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: "800",
                    color: "#020617",
                    lineHeight: "1.25",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={displayTitle}
                >
                  {displayTitle}
                </div>

                {/* Row 2: AssetId -> Media category -> Season Number episode number */}
                <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                  {displayAssetId && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "600",
                        color: "#475569",
                        backgroundColor: "#f1f5f9",
                        border: "1px solid #cbd5e1",
                        borderRadius: "12px",
                        padding: "1px 8px",
                      }}
                    >
                      #{displayAssetId}
                    </span>
                  )}
                  {displayCategoryName && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        color: "#334155",
                        backgroundColor: "#edf2f7",
                        border: "1px solid #cbd5e1",
                        borderRadius: "12px",
                        padding: "1px 8px",
                      }}
                    >
                      {displayCategoryName}
                    </span>
                  )}
                  {displaySeasonEp && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        color: "#c2410c",
                        backgroundColor: "#ffedd5",
                        border: "1px solid #fed7aa",
                        borderRadius: "12px",
                        padding: "1px 8px",
                      }}
                    >
                      {displaySeasonEp}
                    </span>
                  )}
                </div>

                {/* Row 3: Slotdatetime -> Channel -> Duration -> segment */}
                <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
                  {displaySlotDateTime && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        color: "#0369a1",
                        backgroundColor: "#e0f2fe",
                        border: "1px solid #bae6fd",
                        borderRadius: "12px",
                        padding: "1px 8px",
                      }}
                    >
                      {displaySlotDateTime}
                    </span>
                  )}
                  {displayChannelName && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        color: "#0369a1",
                        backgroundColor: "#e0f2fe",
                        border: "1px solid #bae6fd",
                        borderRadius: "12px",
                        padding: "1px 8px",
                      }}
                    >
                      {displayChannelName}
                    </span>
                  )}
                  {displayDuration && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        color: "#15803d",
                        backgroundColor: "#dcfce7",
                        border: "1px solid #bbf7d0",
                        borderRadius: "12px",
                        padding: "1px 8px",
                      }}
                    >
                      {displayDuration}
                    </span>
                  )}
                  {displaySegmentName && (
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: "500",
                        color: "#6b21a8",
                        backgroundColor: "#f3e8ff",
                        border: "1px solid #e9d5ff",
                        borderRadius: "12px",
                        padding: "1px 8px",
                      }}
                    >
                      {displaySegmentName}
                    </span>
                  )}
                </div>

                {/* Row 4: Description */}
                {displaySynopsis && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#64748b",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={displaySynopsis}
                  >
                    {displaySynopsis}
                  </div>
                )}
              </div>

              <div style={{ display: "flex", flexDirection: "column", flex: 1, height: "100%" }}>
                <div style={{ height: "auto", display: "flex", flexDirection: "column", flex: 1, justifyContent: "space-between", margin: 0, padding: "12px", border: "1px solid #e2e8f0", borderRadius: "10px", backgroundColor: "#f8fafc", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                  <div className="mb-2" style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                    <Field
                      name={"mediaEpisode.AssetId"}
                      component={Input}
                      label={lang.house_number_label}
                      disabled
                    />
                  </div>
                  <div className="row mb-2" style={{ marginLeft: "-4px", marginRight: "-4px" }}>
                    <div className={"col-12 mb-2"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field name={"Title"} component={Input} label={lang.title_label} disabled />
                    </div>

                    <div className={"col-12 mb-2"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <label htmlFor="customTitle">{lang.custom_title_label}</label>
                      <Field name={"customTitle"} component={Input} />
                    </div>

                    <div className={"col-12 mb-2"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field name={"Description"} component={Input} label={lang.description_label} disabled />
                    </div>

                    <div className={"col-12 mb-2"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <label htmlFor="customTitle">{lang.custom_description_label}</label>
                      <Field name={"SlotSynopsis"} component={Input} />
                    </div>

                    {isEpisode && <div className={"col-6"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field
                        name={"SeasonNumber"}
                        component={NumericTextBox}
                        label={lang.season_no_label}
                        disabled
                      />
                    </div>}
                    {isEpisode && <div className={"col-6"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field
                        name={"EpisodeNumber"}
                        component={NumericTextBox}
                        label={lang.episode_number_label}
                        disabled
                      />
                    </div>}
                  </div>
                  {isEpisode && <div className="mb-2" style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                    <Field
                      name={"mediaEpisode.EpisodeTitle"}
                      component={Input}
                      label={lang.episode_title_label}
                      disabled
                    />
                  </div>}
                  <div className="row mb-2" style={{ marginLeft: "-4px", marginRight: "-4px" }}>
                    <div className={"col-12"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field
                        name={"mediaEpisode.MediaCategory.Description"}
                        component={Input}
                        label={lang.category_type_label}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="row mb-2" style={{ marginLeft: "-4px", marginRight: "-4px" }}>
                    <div className={"col-6"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field
                        name={"ExhibitionNumber"}
                        component={NumericTextBox}
                        label={lang.exhibition_label}
                        disabled
                      />
                    </div>
                    <div className={"col-6"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field
                        name={"RepeatNumber"}
                        component={NumericTextBox}
                        label={lang.repeat_label}
                        disabled
                      />
                    </div>
                  </div>
                  <div className="row mb-2" style={{ marginLeft: "-4px", marginRight: "-4px" }}>
                    <div className="col-4" style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field
                        name={"ChannelSID"}
                        component={DropdownWithDefaultValue}
                        data={channels}
                        datakey="SID"
                        textField={'FullChannelName'}
                        label={lang.channel_label}
                        disabled
                      />
                    </div>
                    <div className={"col-4"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field
                        name={"SlotDateLocal"}
                        component={DatePicker}
                        placeholder={""}
                        label={"Slot Date"}
                      />
                    </div>
                    <div className={"col-4"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field
                        name={"SlotTimeLocal"}
                        component={TimePickerWithFormat}
                        label={lang.slot_time_label}
                      />
                    </div>
                  </div>
                  <div className="row mb-2" style={{ marginLeft: "-4px", marginRight: "-4px" }}>
                    <div className={"col-6"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field
                        name={"ActualDuration"}
                        component={TimePickerWithFormat}
                        label={lang.actual_duration_label}
                        disabled={isOpenFromSchedule}
                      />
                    </div>

                    <div className={"col-6"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field
                        name={"NominalDuration"}
                        component={TimePickerWithFormat}
                        label={lang.nominal_duration_label}
                        disabled={isOpenFromSchedule}
                      />
                    </div>

                    <div className={"col-6 mt-2"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field
                        name={"customDuration"}
                        component={TimePickerWithFormat}
                        label={'Custom Duration'}
                      />
                    </div>

                    <div className={"col-6 mt-2"} style={{ paddingLeft: "4px", paddingRight: "4px" }}>
                      <Field
                        name={"selectedSegment"}
                        component={DropdownWithDefaultValue}
                        data={segments}
                        textField={"SegmentType.Description"}
                        label={lang.segments_label}
                        datakey="_id"
                        disabled={isOpenFromSchedule}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </FormElement>
          );
        }}
      />
    </BossDialog>
  );
};

export default PlanningRightSideEditFormWithSlot;
